import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { handleSSOCallback, extractUserFromSSO, initiateSSOLogin } from '@/services/ssoService';
import { auth } from '@/firebase/config';
import { findOrganizationBySSODomain, extractDomainFromEmail } from '@/services/organizationSSOService';
import { organizationService } from '@/firebase/organizationService';
import { userService } from '@/firebase/database';
import { redirectToMainDomain } from '@/utils/domainUtils';
import { trackAuth } from '@/utils/analytics';
import { AlertCircle, CheckCircle } from 'lucide-react';

const SSOCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { login, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'initiating' | 'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing SSO authentication...');

  useEffect(() => {
    const processSSOCallback = async () => {
      // If user is already authenticated and we don't have SSO session data,
      // redirect to home (user already logged in via another method or page reload)
      if (isAuthenticated) {
        const hasSSOSession = sessionStorage.getItem('sso_org_id');
        if (!hasSSOSession) {
          console.log('✅ User already authenticated, redirecting to home...');
          redirectToMainDomain('/home');
          return;
        }
      }
      // FIRST: Check if we have a redirect result or authenticated user from Firebase Auth
      // This means we're returning from the IdP
      try {
        const firebaseUser = await handleSSOCallback();
        if (firebaseUser) {
          // We have a user (either from redirect result or already authenticated) - process it
          console.log('✅ Found authenticated user, processing callback...');
          // Continue to callback processing below
        } else {
          // No user found - check if we need to initiate
          const shouldInitiate = searchParams.get('initiate') === 'true';
          
          if (shouldInitiate) {
            // Check if we already initiated (prevent loop)
            const alreadyInitiated = sessionStorage.getItem('sso_initiated');
            if (alreadyInitiated === 'true') {
              // Check if user is already authenticated (redirect result may have been consumed)
              const tenantId = sessionStorage.getItem('sso_tenant_id');
              // Set tenant ID on auth instance if needed
              if (tenantId && auth) {
                (auth as any).tenantId = tenantId;
              }
              const authInstance = auth;
              
              if (authInstance?.currentUser) {
                // User is authenticated! Process the callback
                console.log('✅ User already authenticated, processing callback...');
                // Continue to callback processing below
              } else {
                // Wait a bit and check again - redirect might still be processing
                console.log('⚠️ SSO already initiated, checking for user...');
                setTimeout(async () => {
                  try {
                    const user = await handleSSOCallback();
                    if (user) {
                      // User found, reload to process
                      window.location.reload();
                    } else {
                      setStatus('error');
                      setMessage('Authentication timed out. Please try again.');
                      sessionStorage.removeItem('sso_initiated');
                      setTimeout(() => navigate('/login'), 3000);
                    }
                  } catch (err) {
                    console.error('Error checking for user:', err);
                  }
                }, 1000);
                setStatus('processing');
                setMessage('Waiting for authentication...');
                return;
              }
            }

            try {
              setStatus('initiating');
              setMessage('Initiating SSO login...');
              
              // Mark as initiated to prevent loop
              sessionStorage.setItem('sso_initiated', 'true');
              
              // Get organization from session
              const storedOrgId = sessionStorage.getItem('sso_org_id');
              if (!storedOrgId) {
                throw new Error('No organization found. Please try logging in again.');
              }

              // Get organization
              const orgResult = await organizationService.getOrganization(storedOrgId);
              if (!orgResult.success || !orgResult.data) {
                throw new Error('Organization not found.');
              }

              const organization = orgResult.data;
              if (!organization.sso?.enabled) {
                throw new Error('SSO not enabled for this organization.');
              }

              // Initiate SSO redirect - this will redirect to IdP
              // When IdP redirects back, it will come to this same page (/sso-callback)
              await initiateSSOLogin(organization);
              return; // Will redirect, so we don't continue
            } catch (error: any) {
              console.error('SSO initiation error:', error);
              setStatus('error');
              setMessage(error.message || 'Failed to initiate SSO login.');
              sessionStorage.removeItem('sso_org_id');
              sessionStorage.removeItem('sso_tenant_id');
              sessionStorage.removeItem('sso_email');
              sessionStorage.removeItem('sso_initiated');
              
              // Redirect to login after 3 seconds
              setTimeout(() => {
                navigate('/login');
              }, 3000);
              return;
            }
          } else {
            // No initiate param and no redirect result - invalid state
            throw new Error('No SSO session found. Please try logging in again.');
          }
        }
      } catch (error: any) {
        // If handleSSOCallback throws, it might be because there's no redirect result
        // Check if we should initiate instead
        if (error.message?.includes('No redirect result') || error.message?.includes('redirect')) {
          const shouldInitiate = searchParams.get('initiate') === 'true';
          if (shouldInitiate) {
            // Continue to initiation logic below (will be handled in the else block)
            // But first, let's check if we already initiated
            const alreadyInitiated = sessionStorage.getItem('sso_initiated');
            if (alreadyInitiated === 'true') {
              console.log('⚠️ SSO already initiated, but no redirect result yet. This might be a timing issue.');
              setStatus('processing');
              setMessage('Processing authentication...');
              return;
            }
          } else {
            throw error; // Re-throw if we shouldn't initiate
          }
        } else {
          throw error; // Re-throw other errors
        }
      }

      // If we reach here, we have a redirect result - process the callback
      try {
        // Get stored org ID from session
        const storedOrgId = sessionStorage.getItem('sso_org_id');

        if (!storedOrgId) {
          throw new Error('No organization found in session. Please try logging in again.');
        }

        // Get organization
        const orgResult = await organizationService.getOrganization(storedOrgId);
        if (!orgResult.success || !orgResult.data) {
          throw new Error('Organization not found.');
        }

        const organization = orgResult.data;
        if (!organization.sso?.enabled) {
          throw new Error('SSO not enabled for this organization.');
        }

        setMessage('Processing SSO authentication...');

        // Get redirect result (we already checked above, but get it again to be sure)
        const firebaseUser = await handleSSOCallback();
        
        if (!firebaseUser) {
          throw new Error('SSO authentication failed. Please try again.');
        }
        
        // Clear initiation flag since we successfully got a result
        sessionStorage.removeItem('sso_initiated');

        // Extract user information from Firebase User
        const ssoUser = extractUserFromSSO(firebaseUser);
        
        if (!ssoUser.email) {
          throw new Error('Could not extract email from SSO response.');
        }

        // Verify email domain matches
        const emailDomain = extractDomainFromEmail(ssoUser.email);
        const ssoDomain = organization.sso.domain;
        if (!ssoDomain) {
          throw new Error('SSO domain not configured for this organization.');
        }
        if (emailDomain?.toLowerCase() !== ssoDomain.toLowerCase()) {
          throw new Error(`Email domain (${emailDomain}) does not match organization SSO domain (${ssoDomain}).`);
        }

        setMessage('SSO authentication successful. Setting up your account...');

        // Check if user document exists in Firestore
        const { doc, getDoc, setDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          // Update existing user document
          const userData = userDoc.data();
          const updateData: any = {
            ...userData,
            name: ssoUser.name,
            email: ssoUser.email.toLowerCase(),
            authProvider: 'sso',
            ssoProvider: organization.sso.provider,
            lastSSOLogin: new Date(),
            updatedAt: new Date(),
          };
          // Only include picture if it's defined
          if (ssoUser.picture) {
            updateData.picture = ssoUser.picture;
          }
          await setDoc(userDocRef, updateData, { merge: true });
        } else {
          // Create new user document
          const newUserData: any = {
            id: firebaseUser.uid,
            name: ssoUser.name,
            email: ssoUser.email.toLowerCase(),
            role: 'user',
            organizationId: organization.id,
            verified_email: true, // SSO emails are verified
            authProvider: 'sso',
            ssoProvider: organization.sso.provider,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          // Only include picture if it's defined
          if (ssoUser.picture) {
            newUserData.picture = ssoUser.picture;
          }
          await setDoc(userDocRef, newUserData);
        }

        // Get updated user data
        const updatedUserDoc = await getDoc(userDocRef);
        const userData = updatedUserDoc.exists() ? updatedUserDoc.data() : {};

        // Create user object for auth context
        const user = {
          id: firebaseUser.uid,
          name: ssoUser.name,
          email: ssoUser.email,
          picture: ssoUser.picture || '',
          verified_email: true,
          organizationId: organization.id || userData.organizationId,
          role: userData.role,
        };

        // Login the user
        await login(user, organization.id);

        // Track SSO login
        trackAuth.login('sso');

        // Clear session storage
        sessionStorage.removeItem('sso_org_id');
        sessionStorage.removeItem('sso_tenant_id');
        sessionStorage.removeItem('sso_email');
        sessionStorage.removeItem('sso_initiated');

        setStatus('success');
        setMessage('Successfully signed in! Redirecting...');

        // Redirect to home
        setTimeout(() => {
          redirectToMainDomain('/home');
        }, 1000);

      } catch (error: any) {
        console.error('SSO callback error:', error);
        setStatus('error');
        setMessage(error.message || 'SSO authentication failed. Please try again.');
        
        // Clear session storage on error
        sessionStorage.removeItem('sso_org_id');
        sessionStorage.removeItem('sso_tenant_id');
        sessionStorage.removeItem('sso_email');
        sessionStorage.removeItem('sso_initiated');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    };

    processSSOCallback();
  }, [login, navigate, isAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-5 h-5 rounded-full bg-black flex-shrink-0"></div>
            <span className="text-xl font-bold text-gray-900 lowercase" style={{ letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>complin</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {(status === 'initiating' || status === 'processing') && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{message}</p>
              {status === 'initiating' && (
                <p className="text-sm text-gray-500 mt-2">Redirecting to your organization's login page...</p>
              )}
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to login page...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SSOCallbackPage;

